import React, { useState } from 'react';
import ArrangeCallbackModal from './ArrangeCallbackModal';

export interface AccountOwnerCardProps {
  accountOwnerName: string;
  accountOwnerEmail?: string;
  accountOwnerImageUrl?: string;
  onArrangeCallback?: (notes?: string) => void;
  isVerticalLayout?: boolean; // New prop to control layout
}

const AccountOwnerCard: React.FC<AccountOwnerCardProps> = ({
  accountOwnerName,
  accountOwnerEmail,
  accountOwnerImageUrl,
  onArrangeCallback,
  isVerticalLayout = false, // Default to horizontal layout
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate initials from name
  const getInitials = (name: string): string => {
    const names = name.trim().split(' ');
    if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const initials = getInitials(accountOwnerName);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitCallback = async (notes?: string) => {
    setIsSubmitting(true);
    try {
      if (onArrangeCallback) {
        await onArrangeCallback(notes);
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error arranging callback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div 
        data-testid="account-owner-card"
        className={`bg-gray-100 rounded-lg border border-gray-200 shadow-sm p-6 ${
          isVerticalLayout 
            ? 'flex flex-col gap-4' 
            : 'flex items-center justify-between gap-6'
        }`}
      >
        {/* Help text section */}
        <div 
          data-testid="help-section" 
          className={isVerticalLayout ? 'w-full' : 'flex-1 min-w-0'}
        >
          <h2 
            data-testid="help-title"
            className="text-lg font-medium text-gray-900 mb-2"
          >
            We&apos;re here to help
          </h2>
          <p 
            data-testid="help-description"
            className="text-gray-600 text-sm leading-relaxed"
          >
            Arrange a call with your account owner to discuss your recommendations, and get your
            tech to where it should be.
          </p>
        </div>

        {/* Owner info section */}
        <div 
          data-testid="owner-info-section"
          className="flex items-center gap-4 flex-shrink-0"
        >
          {/* Avatar */}
          {accountOwnerImageUrl ? (
            <img
              data-testid="owner-avatar-image"
              src={accountOwnerImageUrl}
              alt={accountOwnerName}
              className="w-20 h-20 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div 
              data-testid="owner-avatar-initials"
              className="w-20 h-20 rounded-full bg-indigo-500 flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0"
            >
              {initials}
            </div>
          )}

          {/* Name, title and button - vertical layout */}
          <div 
            data-testid="owner-details"
            className="flex flex-col items-start"
          >
            {/* Name and title */}
            <div data-testid="owner-info" className="mb-2">
              <h3 
                data-testid="owner-name"
                className="text-sm font-semibold text-gray-900 leading-tight whitespace-nowrap"
              >
                {accountOwnerName}
              </h3>
              <p 
                data-testid="owner-title"
                className="text-sm text-gray-600"
              >
                Account owner
              </p>
            </div>

            {/* CTA Button */}
            <button
              data-testid="arrange-callback-button"
              onClick={handleOpenModal}
              className="bg-[#00ec7b] hover:bg-[rgb(12,212,115)] text-gray-700 font-medium px-4 py-2 rounded-full transition-colors duration-200 text-sm whitespace-nowrap flex items-center justify-center"
            >
              Arrange a callback
            </button>
          </div>
        </div>
      </div>

      {/* Arrange Callback Modal */}
      <ArrangeCallbackModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitCallback}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default AccountOwnerCard;